import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import React, { PureComponent } from 'react';

import transform from '../transform';
import defaultSettings from './settings';
import userSettings from './userSettings';
import {
    GA_CHOOSE_PACKAGE,
    GA_NOTIFICATION_BANNER_VIEWED,
    GA_P1_LABEL,
    GA_P2_LABEL,
    GA_P3_LABEL,
    GA_PACKAGE_SELECTED,
    GA_PACKAGE_VIEWED,
    PRICING_COMPARE_PACKAGES_ACTION,
    PRICING_PACKAGE_CTA_ACTION,
    PRICING_PAGE_VIEW,
} from '../../constants/analytics';
import {
    ESSENTIAL_V2_UPDATE_URL,
    LOCALE_WITH_VERSION_2,
    PACKAGE_ONE,
    PACKAGE_TWO,
    PRICING_PAGE,
} from '../../constants/main';
import {
    UPDATE_BANNER_CTA,
    UPDATE_BANNER_HEADING,
} from '../../constants/update';
import {
    canUpdateEssentialsV2,
    handleLoadDriftChatbot,
    isActiveForAU,
    trackAnalyticsEventAU,
    verifyCountryCrazyEgg,
} from '../utils';
import { AU_LOCALE } from 'js-utils/intl';
import { logCheckpointViaAjax } from '@eb/janus';
import { generateAdditionalSettings } from '../changePlan/additionalSettings';

import PageTitle from '../../components/PageTitle';
import Faqs from '../../components/Faqs';
import Packages from '../../components/Packages';
import FeaturesComparison from '../../components/FeaturesComparison';
import PricingCalculatorSection from '../../components/PricingCalculatorSection';
import PackageUpdateBanner from '../../components/PackageUpdateBanner';

import './page.scss';

export default class PricingPage extends PureComponent {
    componentDidMount() {
        const {
            isAuthenticated,
            tld,
            launchPackageEssentialV2,
            locale,
        } = this.props;

        handleLoadDriftChatbot(tld, isAuthenticated);

        logCheckpointViaAjax(PRICING_PAGE_VIEW);

        if (isActiveForAU({ locale, launchPackageEssentialV2 })) {
            this._trackEventActionsAU(GA_PACKAGE_VIEWED);
        } else if (locale === AU_LOCALE && !launchPackageEssentialV2) {
            this._trackEventActionsAU(GA_PACKAGE_SELECTED);
        }

        //Crazy Egg
        verifyCountryCrazyEgg(locale);
    }

    componentWillUnmount() {
        const { hideMainNotification } = this.props;
        hideMainNotification();
    }

    _trackEventActionsAU(action) {
        const {
            assortmentsAvailability,
            launchPackageEssentialV2,
            locale,
            userId,
            userPlan,
        } = this.props;
        const version = assortmentsAvailability
            ? assortmentsAvailability.current.version
            : null;
        trackAnalyticsEventAU({
            action: action,
            category: GA_CHOOSE_PACKAGE,
            dimensions: {
                userId,
                userPlan,
                version,
                locale,
            },
        });
    }

    render() {
        const {
            locale,
            isAuthenticated,
            addMainNotification,
            assortmentsAvailability,
            assortmentsPricing,
            assortmentsTax,
            currencySymbol,
            localeDefaultPlan,
            feeBreakdown,
            hasAssortmentsTax,
            redirectToChangePlan,
            redirectToEssentials,
            redirectToProfessional,
            redirectToPremium,
            redirectToCreate,
            hasFixedFee,
            goToPackageComparison,
            userPlan,
            packageTitles,
            selectedPackage,
            clearSelectedPackage,
            showLowAtvUi,
            cappedFeeExperimentFeatureFlag,
            enablePricingCalculator,
            trackEvent,
            defaultCurrency,
            currencyFormat,
            launchPackageEssentialV2,
            useRefactoredPricingTableComparison,
            goToOverviewPage,
        } = this.props;
        const additionalSettings = generateAdditionalSettings({
            locale,
            isAuthenticated,
            assortmentsAvailability,
            launchPackageEssentialV2,
        });
        const {
            packagesList,
            faqs,
            grid,
            paymentProcessingFeature,
            sections,
        } = transform({
            locale,
            isAuthenticated,
            userPlan,
            defaultSettings,
            userSettings,
            page: PRICING_PAGE,
            additionalSettings,
        });
        const learnMoreActions = Array(3).fill(goToOverviewPage);
        const CTAActions = [
            redirectToEssentials.bind(
                null,
                PRICING_PACKAGE_CTA_ACTION,
                GA_P1_LABEL,
            ),
            redirectToProfessional.bind(
                null,
                PRICING_PACKAGE_CTA_ACTION,
                GA_P2_LABEL,
            ),
            redirectToPremium.bind(
                null,
                PRICING_PACKAGE_CTA_ACTION,
                GA_P3_LABEL,
            ),
        ];

        const gridCTAActions = [
            redirectToEssentials,
            redirectToProfessional,
            redirectToPremium,
        ];

        const priceCalculatorCreateActionsMap = {
            [PACKAGE_ONE]: isAuthenticated
                ? redirectToCreate
                : redirectToEssentials,
            [PACKAGE_TWO]: isAuthenticated
                ? redirectToCreate
                : redirectToProfessional,
        };

        const formattedFeeBreakdown = useRefactoredPricingTableComparison
            ? mapValues(feeBreakdown, (itemType) =>
                  merge(itemType, grid.feeBreakdownOverride),
              )
            : merge(feeBreakdown, grid.feeBreakdownOverride);

        // Hide Learn More component if locale includes Essentials new version
        const shouldShowLearnMore = !(
            LOCALE_WITH_VERSION_2.includes(locale) && launchPackageEssentialV2
        );

        // const faqs

        const faqsSection = (
            <Faqs
                {...faqs}
                onCTAClick={goToPackageComparison.bind(
                    null,
                    '',
                    PRICING_COMPARE_PACKAGES_ACTION,
                )}
                locale={locale}
                pricing={assortmentsPricing}
                hasAssortmentsTax={hasAssortmentsTax}
                showCappedFee={cappedFeeExperimentFeatureFlag}
                feeBreakdown={
                    formattedFeeBreakdown.ticket || formattedFeeBreakdown
                }
                useRefactoredPricingTableComparison={
                    useRefactoredPricingTableComparison
                }
            />
        );

        let finePrintBlock;
        let pricingCalculator;

        if (grid.showFinePrint) {
            finePrintBlock = (
                <p className="eds-g-cell eds-g-cell-1-1 eds-text--center eds-text-bs eds-text-color--grey-600 eds-l-pad-vert-5">
                    {grid.finePrint}
                </p>
            );
        }

        if (canUpdateEssentialsV2({ locale, assortmentsAvailability })) {
            if (isActiveForAU({ locale, launchPackageEssentialV2 })) {
                this._trackEventActionsAU(GA_NOTIFICATION_BANNER_VIEWED);
            }

            const updatePlanContent = (
                <PackageUpdateBanner
                    heading={UPDATE_BANNER_HEADING}
                    bannerCTA={UPDATE_BANNER_CTA}
                    redirectUrl={ESSENTIAL_V2_UPDATE_URL}
                />
            );
            addMainNotification({
                children: updatePlanContent,
                hasCloseButton: true,
                shouldPersist: true,
                isNotificationSticky: true,
            });
        }

        if (enablePricingCalculator) {
            pricingCalculator = (
                <PricingCalculatorSection
                    localeDefaultPlan={localeDefaultPlan}
                    currencySymbol={currencySymbol}
                    createActions={priceCalculatorCreateActionsMap}
                    isAuthenticated={isAuthenticated}
                    handleChangePlan={redirectToChangePlan}
                    userPlan={userPlan}
                    trackEvent={trackEvent}
                    defaultCurrency={defaultCurrency}
                    currencyFormat={currencyFormat}
                />
            );
        }

        return (
            <div
                className="eds-g-grid eds-bg-color--grey-100"
                data-automation="alp-pricing"
            >
                <PageTitle title={packageTitles.pricing} />
                {pricingCalculator}
                {showLowAtvUi ? (
                    <div className="eds-g-cell pricing-page-feature-grid">
                        <FeaturesComparison
                            locale={locale}
                            packages={grid.packages}
                            packageTypes={grid.packageTypes}
                            feeBreakdown={formattedFeeBreakdown}
                            assortmentsTax={assortmentsTax}
                            primaryHeading={grid.primaryHeading}
                            secondaryHeading={grid.secondaryHeading}
                            featureDescriptions={grid.featureDescriptions}
                            paymentProcessingFeature={paymentProcessingFeature}
                            sections={sections}
                            selectedPackage={selectedPackage}
                            learnMoreActions={learnMoreActions}
                            getStartedActions={gridCTAActions}
                            clearSelectedPackage={clearSelectedPackage}
                            CTAs={grid.CTAs}
                            CTAStyles={grid.CTAStyles}
                            isSimplePricing={true}
                            extraMainFeatures={grid.extraMainFeatures}
                            pricePerPaidTicket={grid.pricePerPaidTicket}
                            pricePerFreeTicket={grid.pricePerFreeTicket}
                            shouldShowAnnualFee={grid.shouldShowAnnualFee}
                            shouldShowCappedFee={cappedFeeExperimentFeatureFlag}
                            useRefactoredPricingTableComparison={
                                useRefactoredPricingTableComparison
                            }
                        />
                        {finePrintBlock}
                    </div>
                ) : (
                    <Packages
                        {...packagesList}
                        pricing={assortmentsPricing}
                        taxLabel={assortmentsTax}
                        hasFixedFee={hasFixedFee}
                        CTAActions={CTAActions}
                        shouldShowLearnMore={shouldShowLearnMore}
                        shouldShowNewTag={isActiveForAU({
                            locale,
                            launchPackageEssentialV2,
                        })}
                        learnMoreActions={learnMoreActions}
                        fixedHeight={true}
                        selectedPackage={selectedPackage}
                    />
                )}
                {faqsSection}
            </div>
        );
    }
}
